import React, { useEffect, useState } from 'react';
import { PhoneIcon, MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, CTAButton } from './shared';
import { BrandLogo } from './BrandLogo';
// 02 — STICKY HEADER · [Journey] [Conversion]
// Max six top-level items. Logo left, nav centred/right, visible phone, high-contrast CTA.
// Accessible mega-menu for Claims. Mobile menu + persistent bottom action bar.
const claimsMenu = [{
  label: 'Work injuries',
  href: '/workers-compensation-lawyers-perth/'
}, {
  label: 'Road accidents',
  href: '/car-accident-lawyers-perth/'
}, {
  label: 'Public liability',
  href: '/public-liability-lawyers-perth/'
}, {
  label: 'Serious & catastrophic injuries',
  href: '/catastrophic-injury-lawyers-perth/'
}, {
  label: 'Other or unsure',
  href: '#claim-router'
}];
const navItems = [{
  label: 'Reviews',
  href: '#reviews'
}, {
  label: 'Our Team',
  href: '#team'
}, {
  label: 'Fees',
  href: '#fees'
}];
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setClaimsOpen(false);
        setMobileOpen(false);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  return <>
      <header className={`sticky top-0 z-40 w-full bg-stone transition-shadow ${scrolled ? 'shadow-[0_1px_0_rgba(27,30,29,0.1)]' : ''}`} data-id="element-276" data-mp="30klmjoaclbnh">
        <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-4 px-5 py-3 sm:px-6 lg:px-8" data-id="element-277" data-mp="3ta2aa8n0w4dd">
          <a href="/" className="flex shrink-0 items-center rounded-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-278" data-mp="1re34nr0oekdr">
            <BrandLogo data-id="element-279" data-mp="1hmyud4ly3np9" />
          </a>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden items-center gap-0 xl:flex" data-id="element-280" data-mp="1mmbcxd5sa8m1">
            <div className="relative" onMouseEnter={() => setClaimsOpen(true)} onMouseLeave={() => setClaimsOpen(false)} data-id="element-281" data-mp="18m0kw6sqmnza">
              <button type="button" aria-expanded={claimsOpen} aria-haspopup="true" onClick={() => setClaimsOpen(v => !v)} className="inline-flex items-center gap-1 rounded-btn px-3 py-2 text-body font-medium text-charcoal hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-282" data-mp="ypfg9mkmrevz">
                Claims
                <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 transition-transform ${claimsOpen ? 'rotate-180' : ''}`} strokeWidth={1.75} data-id="element-283" data-mp="mf69kmz3ew3w" />
              </button>
              {claimsOpen && <div className="absolute left-0 top-full w-72 rounded-card border border-charcoal/10 bg-stone p-2 shadow-lg" data-id="element-284" data-mp="rdhnb8sidqre">
                  {claimsMenu.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-3 py-2.5 text-body text-charcoal hover:bg-eucalypt/5 hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-285" data-mp="106bd405l08ji">
                      {item.label}
                    </a>)}
                </div>}
            </div>
            {navItems.map(item => <a key={item.label} href={item.href} className="rounded-btn px-3 py-2 text-body font-medium text-charcoal hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-286" data-mp="22yxehihxvio7">
                {item.label}
              </a>)}
          </nav>

          <div className="hidden items-center gap-3 xl:flex" data-id="element-287" data-mp="2gxwuk1rdyp8r">
            <a href={PHONE_TEL} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn px-3 text-[15px] font-semibold text-forest transition-colors duration-150 ease-out hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-288" data-mp="1k8ywoaocab7d">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="1y15es2b1ys9g" />
              {PHONE_DISPLAY}
            </a>
            <CTAButton href="#compensation-calculator" className="px-5" data-id="element-290" data-mp="11flkfmseoth3">
              Start free claim check
            </CTAButton>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-1 xl:hidden" data-id="element-291" data-mp="xz8o74qo7yol">
            <a href={PHONE_TEL} aria-label={`Call ${PHONE_DISPLAY}`} className="inline-flex h-11 w-11 items-center justify-center rounded-btn text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-292" data-mp="t0fja0pg9g01">
              <PhoneIcon aria-hidden="true" className="h-5 w-5" strokeWidth={1.75} data-id="element-293" data-mp="f0mn3pv5m7ko" />
            </a>
            <button type="button" aria-expanded={mobileOpen} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} onClick={() => setMobileOpen(v => !v)} className="inline-flex h-11 w-11 items-center justify-center rounded-btn text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-294" data-mp="a8376m8zv0cp">
              {mobileOpen ? <XIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-295" data-mp="3dtdem4jog80g" /> : <MenuIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-296" data-mp="3is6vm6y6ah5d" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && <nav aria-label="Mobile" className="border-t border-charcoal/10 bg-stone px-5 pb-6 pt-2 xl:hidden" data-id="element-297" data-mp="33xaet5caslgo">
            <p className="px-1 pb-1 pt-3 text-small font-semibold uppercase tracking-wider text-ochre" data-id="element-298" data-mp="f8ptjep8u3a">
              Claims
            </p>
            {claimsMenu.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-1 py-2.5 text-body-lg text-charcoal hover:text-eucalypt" data-id="element-299" data-mp="3muboh3gsr9qy">
                {item.label}
              </a>)}
            <div className="my-2 h-px bg-charcoal/10" data-id="element-300" data-mp="jb8kx32z4dr3" />
            {navItems.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-1 py-2.5 text-body-lg text-charcoal hover:text-eucalypt" data-id="element-301" data-mp="o5uyncwj2iec">
                {item.label}
              </a>)}
          </nav>}
      </header>

      {/* Mobile bottom action bar — persistent, unobtrusive */}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-px border-t border-charcoal/10 bg-charcoal/10 xl:hidden" data-id="element-302" data-mp="2dm8bnfgwexiu">
        <a href={PHONE_TEL} className="flex min-h-[52px] items-center justify-center gap-2 bg-stone text-body font-semibold text-eucalypt" data-id="element-303" data-mp="1px5xwipdxotm">
          <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-304" data-mp="1yxkkrmhzzbz8" />
          Call {PHONE_DISPLAY}
        </a>
        <a href="#compensation-calculator" className="flex min-h-[52px] items-center justify-center gap-2 bg-signal px-3 text-center text-small font-semibold leading-tight text-paper" data-id="element-305" data-mp="3aaeogpnz4w87">
          Start free claim check
        </a>
      </div>
    </>;
}